import { fetchJson } from '../lib/fetchJson';
import type { Station, TideExtreme, TidePredictionPoint, TidePredictions } from '../types/stations';

const STATIONS_URL = 'https://api.tidesandcurrents.noaa.gov/mdapi/prod/webapi/stations.json?type=waterlevels';
const DATAGETTER_URL = 'https://api.tidesandcurrents.noaa.gov/api/prod/datagetter';

interface StationDTO {
  id: string;
  name: string;
  lat: number;
  lng: number;
  state?: string | null;
  timezone?: string | null;
  tideType?: string | null;
  affiliations?: string | null;
  shefcode?: string | null;
  tidal?: boolean | null;
  greatlakes?: boolean | null;
  forecast?: boolean | null;
  stormsurge?: boolean | null;
}

const blankToNull = (s: string | null | undefined) => (s == null || s === '' ? null : s);

export function stationFromDTO(dto: StationDTO): Station {
  return {
    id: dto.id,
    name: dto.name,
    lat: dto.lat,
    lng: dto.lng,
    state: blankToNull(dto.state),
    timezone: blankToNull(dto.timezone),
    tideType: blankToNull(dto.tideType),
    affiliations: blankToNull(dto.affiliations),
    shefCode: blankToNull(dto.shefcode),
    isTidal: dto.tidal ?? false,
    isGreatLakes: dto.greatlakes ?? false,
    hasForecast: dto.forecast ?? false,
    hasStormSurge: dto.stormsurge ?? false,
  };
}

/** The full national water-level station list in one request (StationService.fetchStations). */
export async function fetchStations(signal?: AbortSignal): Promise<Station[]> {
  const data = await fetchJson<{ stations: StationDTO[] }>(STATIONS_URL, { signal, timeoutMs: 60_000 });
  return (data.stations ?? []).filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lng)).map(stationFromDTO);
}

interface PredictionDTO {
  t: string;
  v: string;
  type?: string;
}

interface PredictionResponse {
  predictions?: PredictionDTO[];
  error?: { message: string };
}

/** "yyyy-MM-dd HH:mm" in station local time, parsed as a local Date like the iOS DateFormatter. */
export function parseCoopsTime(t: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/.exec(t);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
}

function yyyymmdd(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}`;
}

export function predictionUrl(stationId: string, interval: 'h' | 'hilo', days: number, today: Date = new Date()): string {
  const params = new URLSearchParams({
    application: 'ENKIWeb',
    product: 'predictions',
    begin_date: yyyymmdd(today),
    range: String(Math.max(1, days) * 24),
    station: stationId,
    datum: 'MLLW',
    time_zone: 'lst_ldt',
    units: 'english',
    interval,
    format: 'json',
  });
  return `${DATAGETTER_URL}?${params.toString()}`;
}

async function fetchPredictionSeries(stationId: string, interval: 'h' | 'hilo', days: number, signal?: AbortSignal): Promise<PredictionDTO[]> {
  const data = await fetchJson<PredictionResponse>(predictionUrl(stationId, interval, days), { signal, timeoutMs: 20_000 });
  if (data.error?.message) throw new Error(data.error.message);
  return data.predictions ?? [];
}

export function parsePredictions(curveDTOs: PredictionDTO[], extremeDTOs: PredictionDTO[]): TidePredictions {
  const curve: TidePredictionPoint[] = [];
  for (const dto of curveDTOs) {
    const date = parseCoopsTime(dto.t);
    const value = Number.parseFloat(dto.v);
    if (date && Number.isFinite(value)) curve.push({ date, t: dto.t, value });
  }
  const extremes: TideExtreme[] = [];
  for (const dto of extremeDTOs) {
    const date = parseCoopsTime(dto.t);
    const value = Number.parseFloat(dto.v);
    if (date && Number.isFinite(value)) extremes.push({ date, t: dto.t, value, isHigh: dto.type === 'H' });
  }
  return { curve, extremes };
}

/** Hourly curve and high/low events for the next `days` days, fetched concurrently. */
export async function fetchTidePredictions(stationId: string, days = 3, signal?: AbortSignal): Promise<TidePredictions> {
  const [curve, extremes] = await Promise.all([
    fetchPredictionSeries(stationId, 'h', days, signal),
    fetchPredictionSeries(stationId, 'hilo', days, signal),
  ]);
  return parsePredictions(curve, extremes);
}
